import React from "react";
import { base44 } from "../api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { TrendingUp, Target, Calendar, DollarSign, Users, ArrowRight, Award } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function Prefinancements() {
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => { try { return await base44.entities.Campaign.filter({ visible_on_site: true }, '-created_date'); } catch { return []; } },
    retry: false
  });

  const activeCampaigns = campaigns.filter(c => c.status === 'active');
  const upcomingCampaigns = campaigns.filter(c => c.status === 'en_preparation');
  const completedCampaigns = campaigns.filter(c => c.status === 'terminee');

  const statusLabels = { en_preparation:"À Venir", active:"En Cours", terminee:"Terminée", annulee:"Annulée" };
  const statusColors = { en_preparation:"bg-yellow-100 text-yellow-800", active:"bg-green-100 text-green-800", terminee:"bg-blue-100 text-blue-800", annulee:"bg-red-100 text-red-800" };

  const CampaignCard = ({ campaign, index }) => {
    const progress = (campaign.raised_amount / campaign.target_amount) * 100;
    const daysRemaining = campaign.end_date ? Math.ceil((new Date(campaign.end_date) - new Date()) / (1000*60*60*24)) : null;
    return (
      <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }}>
        <div className="overflow-hidden border-none shadow-xl hover:shadow-2xl transition-all h-full rounded-xl bg-white flex flex-col">
          <div className="relative h-48 bg-gradient-to-br from-black to-gray-800 overflow-hidden">
            {campaign.featured_image ? <img src={campaign.featured_image} alt={campaign.title} className="w-full h-full object-cover opacity-70" />
              : <div className="w-full h-full flex items-center justify-center"><TrendingUp className="w-20 h-20 text-white/30" /></div>}
            <div className="absolute top-4 right-4"><span className={`${statusColors[campaign.status]} px-3 py-1 rounded-full text-sm font-bold`}>{statusLabels[campaign.status]}</span></div>
            {daysRemaining !== null && daysRemaining > 0 && campaign.status === 'active' && (
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                <p className="text-xs text-gray-600">Temps restant</p>
                <p className="text-lg font-bold text-black">{daysRemaining} jours</p>
              </div>
            )}
          </div>
          <div className="p-6 flex flex-col flex-1">
            <h3 className="text-2xl font-bold mb-3 text-black">{campaign.title}</h3>
            <p className="text-gray-600 mb-4 line-clamp-2">{campaign.description}</p>
            {campaign.status === 'active' && (
              <>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Progression</span>
                    <span className="font-bold text-black">{progress.toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="accent-gradient h-3 rounded-full" style={{ width:`${Math.min(progress,100)}%` }}></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div className="bg-gray-50 p-3 rounded-lg"><div className="flex items-center gap-2 text-gray-500 mb-1"><Target className="w-4 h-4" /><span>Objectif</span></div><p className="font-bold text-black">{campaign.target_amount?.toLocaleString()} XOF</p></div>
                  <div className="bg-green-50 p-3 rounded-lg"><div className="flex items-center gap-2 text-gray-500 mb-1"><DollarSign className="w-4 h-4" /><span>Collecté</span></div><p className="font-bold text-green-600">{campaign.raised_amount?.toLocaleString()} XOF</p></div>
                  <div className="bg-blue-50 p-3 rounded-lg"><div className="flex items-center gap-2 text-gray-500 mb-1"><Users className="w-4 h-4" /><span>Minimum</span></div><p className="font-bold text-blue-600">{campaign.min_contribution?.toLocaleString()} XOF</p></div>
                  <div className="bg-yellow-50 p-3 rounded-lg"><div className="flex items-center gap-2 text-gray-500 mb-1"><Award className="w-4 h-4" /><span>Récompense</span></div><p className="font-bold text-yellow-600">{campaign.reward_per_1000} m²/1000</p></div>
                </div>
              </>
            )}
            {campaign.start_date && campaign.end_date && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(campaign.start_date),'dd MMM',{locale:fr})} - {format(new Date(campaign.end_date),'dd MMM yyyy',{locale:fr})}</span>
              </div>
            )}
            <div className="mt-auto">
              <Link to={`/campaigndetails?id=${campaign.id}`}>
                <button className="w-full accent-gradient text-white py-3 rounded-lg hover:opacity-90 flex items-center justify-center gap-2">
                  {campaign.status === 'active' ? 'Participer Maintenant' : 'Voir les Détails'}<ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} className="text-center">
            <TrendingUp className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Préfinancement Participatif</h1>
            <p className="text-xl text-gray-200">Investissez ensemble, obtenez votre terrain proportionnel</p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-black mb-12">Comment ça marche ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[{step:"1",title:"Choisissez",desc:"Sélectionnez une campagne active"},{step:"2",title:"Participez",desc:"Investissez le montant de votre choix"},{step:"3",title:"Suivez",desc:"Suivez la progression en temps réel"},{step:"4",title:"Recevez",desc:"Obtenez votre terrain proportionnel"}].map((item,index) => (
              <motion.div key={index} initial={{ opacity:0,y:20 }} whileInView={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }} viewport={{ once:true }} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 accent-gradient rounded-full flex items-center justify-center text-white text-2xl font-bold">{item.step}</div>
                <h3 className="text-xl font-bold text-black mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {activeCampaigns.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-black mb-8">Campagnes en Cours</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {activeCampaigns.map((campaign, index) => <CampaignCard key={campaign.id} campaign={campaign} index={index} />)}
            </div>
          </div>
        </section>
      )}

      {upcomingCampaigns.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-black mb-8">Campagnes à Venir</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingCampaigns.map((campaign, index) => <CampaignCard key={campaign.id} campaign={campaign} index={index} />)}
            </div>
          </div>
        </section>
      )}

      {completedCampaigns.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-black mb-8">Campagnes Terminées</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {completedCampaigns.map((campaign, index) => <CampaignCard key={campaign.id} campaign={campaign} index={index} />)}
            </div>
          </div>
        </section>
      )}

      {campaigns.length === 0 && !isLoading && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-xl text-gray-600">Aucune campagne disponible pour le moment</p>
          </div>
        </section>
      )}
    </div>
  );
}
